import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  Grid,
  Snackbar,
  Alert,
  Tooltip,
  Divider,
  Autocomplete,
  Stack,
} from '@mui/material';
import { Add, Edit, Delete, Calculate, Public } from '@mui/icons-material';
import { tariffService, partnerService } from '../services/api';
import { Tariff, TariffRequest, ChargeBreakdown, FeeType, ChargeBearer } from '../types/tariff';

interface MerchantOption {
  id: string;
  partnerCode: string;
  partnerName: string;
  global?: boolean;
}

// Sentinel option used in the create dialog to mean "no specific merchant" — a global tariff.
const GLOBAL_OPTION: MerchantOption = {
  id: 'GLOBAL',
  partnerCode: 'GLOBAL',
  partnerName: 'Global (all merchants)',
  global: true,
};

const emptyForm: TariffRequest = {
  name: '',
  currency: '',
  feeType: 'PERCENTAGE',
  pspFee: 0.02,
  retailerFee: 0.01,
  chargeBearer: 'MERCHANT',
};

const optionLabel = (o: MerchantOption) =>
  o.global ? o.partnerName : `${o.partnerName} (${o.partnerCode})`;

const TariffManagement: React.FC = () => {
  const [merchants, setMerchants] = useState<MerchantOption[]>([]);
  const [filterMerchant, setFilterMerchant] = useState<MerchantOption | null>(null);

  const [tariffs, setTariffs] = useState<Tariff[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [loading, setLoading] = useState(false);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<TariffRequest>(emptyForm);
  const [formScope, setFormScope] = useState<MerchantOption | null>(null);

  const [simOpen, setSimOpen] = useState(false);
  const [simMerchant, setSimMerchant] = useState<MerchantOption | null>(null);
  const [preview, setPreview] = useState<ChargeBreakdown | null>(null);
  const [previewAmount, setPreviewAmount] = useState('1000');
  const [previewCurrency, setPreviewCurrency] = useState('ZAR');

  const [snack, setSnack] = useState<{ open: boolean; msg: string; severity: 'success' | 'error' }>({
    open: false,
    msg: '',
    severity: 'success',
  });

  const notify = (msg: string, severity: 'success' | 'error' = 'success') =>
    setSnack({ open: true, msg, severity });

  // Load the merchant list once (used by both filter and create/simulator selects).
  useEffect(() => {
    (async () => {
      try {
        const data = await partnerService.getPartners({ partnerType: 'MERCHANT', size: 1000 });
        const list = data?.content || data?.data || data || [];
        const opts: MerchantOption[] = (Array.isArray(list) ? list : []).map((p: any) => ({
          id: String(p.id),
          partnerCode: p.partnerCode,
          partnerName: p.partnerName,
        }));
        setMerchants(opts);
      } catch (e: any) {
        notify(e?.response?.data?.message || 'Failed to load merchants', 'error');
      }
    })();
  }, []);

  const loadTariffs = useCallback(async () => {
    setLoading(true);
    try {
      const data = await tariffService.getTariffs({
        partner: filterMerchant?.partnerCode,
        page,
        size: rowsPerPage,
      });
      // Backend returns a Spring Page; tolerate a plain array too.
      if (Array.isArray(data)) {
        setTariffs(data);
        setTotal(data.length);
      } else {
        setTariffs(data?.content || []);
        setTotal(typeof data?.totalElements === 'number' ? data.totalElements : 0);
      }
    } catch (e: any) {
      notify(e?.response?.data?.message || 'Failed to load tariffs', 'error');
    } finally {
      setLoading(false);
    }
  }, [filterMerchant, page, rowsPerPage]);

  useEffect(() => {
    loadTariffs();
  }, [loadTariffs]);

  const openCreate = () => {
    setEditingId(null);
    setForm({ ...emptyForm });
    // Default the new tariff's scope to whatever is currently filtered, if any.
    setFormScope(filterMerchant);
    setDialogOpen(true);
  };

  const openEdit = (t: Tariff) => {
    setEditingId(t.id);
    setForm({
      name: t.name || '',
      currency: t.currency || '',
      feeType: t.feeType,
      pspFee: t.pspFee,
      retailerFee: t.retailerFee,
      chargeBearer: t.chargeBearer,
    });
    setFormScope(
      t.global
        ? GLOBAL_OPTION
        : merchants.find((m) => m.partnerCode === t.partnerCode) ||
            (t.partnerCode
              ? { id: t.partnerId, partnerCode: t.partnerCode, partnerName: t.partnerCode }
              : null),
    );
    setDialogOpen(true);
  };

  const save = async () => {
    if (form.feeType === 'FIXED' && !form.currency) {
      notify('A FIXED tariff must specify a currency', 'error');
      return;
    }
    if (!editingId && !formScope) {
      notify('Choose a merchant or select Global', 'error');
      return;
    }
    try {
      const isGlobal = !!formScope?.global;
      const payload: TariffRequest = {
        ...form,
        global: isGlobal,
        partnerCode: isGlobal ? undefined : formScope?.partnerCode,
        partnerId: isGlobal ? undefined : formScope?.partnerCode,
        currency: form.currency ? form.currency.toUpperCase() : undefined,
        pspFee: Number(form.pspFee),
        retailerFee: Number(form.retailerFee),
      };
      if (editingId) {
        await tariffService.updateTariff(editingId, payload);
        notify('Tariff updated');
      } else {
        await tariffService.createTariff(payload);
        notify('Tariff created');
      }
      setDialogOpen(false);
      await loadTariffs();
    } catch (e: any) {
      notify(e?.response?.data?.message || 'Failed to save tariff', 'error');
    }
  };

  const deactivate = async (t: Tariff) => {
    try {
      await tariffService.deactivateTariff(t.id);
      notify('Tariff deactivated');
      await loadTariffs();
    } catch (e: any) {
      notify(e?.response?.data?.message || 'Failed to deactivate tariff', 'error');
    }
  };

  const runPreview = async () => {
    if (!simMerchant || simMerchant.global) {
      notify('Pick a specific merchant to simulate', 'error');
      return;
    }
    try {
      const data = await tariffService.previewCharge(
        simMerchant.partnerCode,
        Number(previewAmount),
        previewCurrency.toUpperCase(),
      );
      setPreview(data);
    } catch (e: any) {
      notify(e?.response?.data?.message || 'Failed to compute preview', 'error');
    }
  };

  const fmtFee = (t: Tariff, value: number) =>
    t.feeType === 'PERCENTAGE' ? `${(value * 100).toFixed(2)}%` : value.toFixed(2);

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Tariff Management
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Configure per-merchant or global fees (fixed or percentage) and who bears them — the merchant
        (deducted from payout) or the customer (add-on surcharge).
      </Typography>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={5}>
            <Autocomplete
              size="small"
              options={merchants}
              getOptionLabel={optionLabel}
              isOptionEqualToValue={(a, b) => a.partnerCode === b.partnerCode}
              value={filterMerchant}
              onChange={(_, v) => {
                setFilterMerchant(v);
                setPage(0);
              }}
              renderInput={(params) => (
                <TextField {...params} label="Filter by merchant (blank = all tariffs)" />
              )}
            />
          </Grid>
          <Grid item xs />
          <Grid item>
            <Button variant="outlined" startIcon={<Calculate />} onClick={() => setSimOpen(true)}>
              Simulator
            </Button>
          </Grid>
          <Grid item>
            <Button variant="contained" startIcon={<Add />} onClick={openCreate}>
              New Tariff
            </Button>
          </Grid>
        </Grid>
      </Paper>

      <TableContainer component={Paper} sx={{ mb: 3 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Scope</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Currency</TableCell>
              <TableCell>Fee Type</TableCell>
              <TableCell>PSP Fee</TableCell>
              <TableCell>Retailer Fee</TableCell>
              <TableCell>Charge Bearer</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {tariffs.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} align="center">
                  <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                    {loading ? 'Loading…' : 'No tariffs found.'}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              tariffs.map((t) => (
                <TableRow key={t.id} hover>
                  <TableCell>
                    {t.global ? (
                      <Chip size="small" icon={<Public />} label="Global" color="info" />
                    ) : (
                      t.partnerCode || '—'
                    )}
                  </TableCell>
                  <TableCell>{t.name || '—'}</TableCell>
                  <TableCell>{t.currency || 'All'}</TableCell>
                  <TableCell>{t.feeType}</TableCell>
                  <TableCell>{fmtFee(t, t.pspFee)}</TableCell>
                  <TableCell>{fmtFee(t, t.retailerFee)}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={t.chargeBearer === 'CUSTOMER' ? 'Customer (surcharge)' : 'Merchant'}
                      color={t.chargeBearer === 'CUSTOMER' ? 'warning' : 'default'}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={t.active ? 'Active' : 'Inactive'}
                      color={t.active ? 'success' : 'default'}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Edit">
                      <IconButton size="small" onClick={() => openEdit(t)}>
                        <Edit fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Deactivate">
                      <span>
                        <IconButton size="small" onClick={() => deactivate(t)} disabled={!t.active}>
                          <Delete fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </TableContainer>

      {/* Create / edit dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingId ? 'Edit Tariff' : 'New Tariff'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <Autocomplete
                options={[GLOBAL_OPTION, ...merchants]}
                getOptionLabel={optionLabel}
                isOptionEqualToValue={(a, b) => a.partnerCode === b.partnerCode}
                value={formScope}
                disabled={!!editingId}
                onChange={(_, v) => setFormScope(v)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Scope (merchant or Global)"
                    helperText={
                      editingId
                        ? 'Scope cannot be changed on an existing tariff'
                        : formScope?.global
                          ? 'Applies to all merchants unless a merchant-specific tariff overrides it'
                          : 'Applies to the selected merchant only'
                    }
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                select
                fullWidth
                label="Fee type"
                value={form.feeType}
                onChange={(e) => setForm({ ...form, feeType: e.target.value as FeeType })}
              >
                <MenuItem value="PERCENTAGE">Percentage</MenuItem>
                <MenuItem value="FIXED">Fixed</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label={`Currency${form.feeType === 'FIXED' ? ' (required)' : ' (blank = all)'}`}
                value={form.currency}
                onChange={(e) => setForm({ ...form, currency: e.target.value })}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                type="number"
                label={form.feeType === 'PERCENTAGE' ? 'PSP fee (e.g. 0.02)' : 'PSP fee (amount)'}
                value={form.pspFee}
                onChange={(e) => setForm({ ...form, pspFee: Number(e.target.value) })}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                type="number"
                label={form.feeType === 'PERCENTAGE' ? 'Retailer fee (e.g. 0.01)' : 'Retailer fee (amount)'}
                value={form.retailerFee}
                onChange={(e) => setForm({ ...form, retailerFee: Number(e.target.value) })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                select
                fullWidth
                label="Charge bearer"
                value={form.chargeBearer}
                onChange={(e) => setForm({ ...form, chargeBearer: e.target.value as ChargeBearer })}
                helperText={
                  form.chargeBearer === 'CUSTOMER'
                    ? 'Customer pays face + fee (add-on surcharge); merchant gets full face value.'
                    : 'Merchant absorbs the fee (deducted from payout); customer pays face value.'
                }
              >
                <MenuItem value="MERCHANT">Merchant (deduct from payout)</MenuItem>
                <MenuItem value="CUSTOMER">Customer (add-on surcharge)</MenuItem>
              </TextField>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={save}>
            {editingId ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Charge breakdown simulator */}
      <Dialog open={simOpen} onClose={() => setSimOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Charge breakdown simulator</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 0.5 }}>
            <Autocomplete
              options={merchants}
              getOptionLabel={optionLabel}
              isOptionEqualToValue={(a, b) => a.partnerCode === b.partnerCode}
              value={simMerchant}
              onChange={(_, v) => setSimMerchant(v)}
              renderInput={(params) => <TextField {...params} label="Merchant" />}
            />
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Face amount"
                  type="number"
                  value={previewAmount}
                  onChange={(e) => setPreviewAmount(e.target.value)}
                />
              </Grid>
              <Grid item xs={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="Currency"
                  value={previewCurrency}
                  onChange={(e) => setPreviewCurrency(e.target.value)}
                />
              </Grid>
              <Grid item xs={2}>
                <Button variant="outlined" startIcon={<Calculate />} onClick={runPreview}>
                  Run
                </Button>
              </Grid>
            </Grid>

            {preview && (
              <Box>
                <Divider sx={{ mb: 1 }} />
                <Grid container spacing={1}>
                  <PreviewRow label="Charge bearer" value={preview.chargeBearer} />
                  <PreviewRow label="Fee type" value={preview.feeType} />
                  <PreviewRow label="Face value" value={preview.faceValue?.toFixed(2)} />
                  <PreviewRow label="PSP fee" value={preview.pspFee?.toFixed(2)} />
                  <PreviewRow label="Retailer fee" value={preview.retailerFee?.toFixed(2)} />
                  <PreviewRow label="Total fee" value={preview.totalFee?.toFixed(2)} />
                  <PreviewRow label="Customer pays" value={preview.amountCustomerPays?.toFixed(2)} highlight />
                  <PreviewRow label="Net to merchant" value={preview.netToMerchant?.toFixed(2)} highlight />
                  <PreviewRow label="Clearing sweep" value={preview.clearingAmount?.toFixed(2)} />
                </Grid>
              </Box>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSimOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack({ ...snack, open: false })}
      >
        <Alert severity={snack.severity} onClose={() => setSnack({ ...snack, open: false })}>
          {snack.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
};

const PreviewRow: React.FC<{ label: string; value?: string; highlight?: boolean }> = ({
  label,
  value,
  highlight,
}) => (
  <>
    <Grid item xs={6} sm={4}>
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
    </Grid>
    <Grid item xs={6} sm={8}>
      <Typography variant="body2" fontWeight={highlight ? 700 : 400}>
        {value ?? '—'}
      </Typography>
    </Grid>
  </>
);

export default TariffManagement;
